import React, { useState, useEffect, useRef } from 'react';
import logo from "../icon/logo.png";

/* ─────────────────────────────────────────────
   DESIGN TOKENS  (Optionstock system)
───────────────────────────────────────────── */
const C = {
  bg:         "#F3F6FA",
  surface:    "#FFFFFF",
  surfaceAlt: "#E9EFF5",
  border:     "#D8E2EC",
  borderHover:"#B9C9DA",
  accent:     "#0E7C5A",
  accentDim:  "rgba(14,124,90,0.08)",
  accentGlow: "rgba(14,124,90,0.18)",
  text:       "#16283F",
  textMid:    "#4C6178",
  textDim:    "#8C9BAC",
  red:        "#C0392B",
  green:      "#16915F",
  blue:       "#2F6690",
  purple:     "#6558A3",
  amber:      "#D68910",
};

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; height: 100%; }
body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100%;
  overflow-x: hidden;
}
a { text-decoration: none; color: inherit; }

/* ── Animations ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes gridFade {
  from { opacity: 0; }
  to   { opacity: .25; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.4; transform:scale(.85); }
}
@keyframes pulseRing {
  0%   { transform: scale(0.85); opacity: .7; }
  100% { transform: scale(1.6);  opacity: 0; }
}
@keyframes drift {
  0%,100% { transform: translateY(0px) translateX(0px); }
  33%     { transform: translateY(-8px) translateX(4px); }
  66%     { transform: translateY(4px) translateX(-6px); }
}
@keyframes scanLine {
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { transform: translateY(800%); opacity: 0; }
}
@keyframes nodePulse {
  0%,100% { box-shadow: 0 0 0 0 ${C.accentGlow}; }
  50%     { box-shadow: 0 0 0 8px transparent; }
}
@keyframes drawLine {
  from { stroke-dashoffset: 200; }
  to   { stroke-dashoffset: 0; }
}

/* ── Grid BG ── */
.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 15%, black 0%, transparent 100%);
}

/* ── Nav ── */
.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}
.tf-nav-links { display:flex; align-items:center; gap:4px; }
.tf-nav-link {
  font-size:13px; font-weight:500; color:${C.textMid};
  padding:6px 12px; border-radius:7px;
  transition:color .15s, background .15s;
}
.tf-nav-link:hover { color:${C.text}; background:rgba(22,40,63,.04); }
.tf-nav-link.active { color:${C.text}; font-weight:600; }
@media(max-width:600px){ .tf-nav-links { display:none; } }

/* ── Buttons ── */
.btn-primary {
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:10px 20px; background:${C.accent}; color:#F3FAF7;
  border:none; border-radius:9px; font-size:13px; font-weight:700;
  font-family:'Public Sans',sans-serif; cursor:pointer; letter-spacing:.01em;
  transition:transform .18s, box-shadow .18s; text-decoration:none;
  position:relative; overflow:hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background:linear-gradient(135deg,rgba(255,255,255,.18) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }

.btn-ghost {
  display:inline-flex; align-items:center; justify-content:center;
  padding:8px 16px; background:transparent; color:${C.textMid};
  border:1px solid ${C.border}; border-radius:8px; font-size:12px; font-weight:600;
  font-family:'Public Sans',sans-serif; cursor:pointer;
  transition:border-color .2s, color .2s; text-decoration:none;
}
.btn-ghost:hover { border-color:${C.borderHover}; color:${C.text}; }

/* ── Badge ── */
.badge {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(14,124,90,.08); border:1px solid rgba(14,124,90,.2);
  border-radius:6px; padding:3px 10px;
  font-family:'IBM Plex Mono',monospace; font-size:10px; color:${C.accent};
  letter-spacing:.05em; text-transform:uppercase;
}
.badge::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:${C.accent}; animation:pulse 1.5s ease-in-out infinite; flex-shrink:0;
}

/* ── Shield hero ── */
.shield-wrap {
  position: relative;
  width: 160px; height: 160px;
  margin: 0 auto 36px;
  display: flex; align-items: center; justify-content: center;
}
.shield-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid ${C.accent}30;
  animation: pulseRing 2.8s ease-out infinite;
}
.shield-ring:nth-child(2) { animation-delay: .9s; }
.shield-ring:nth-child(3) { animation-delay: 1.8s; }
.shield-icon {
  width: 88px; height: 88px; border-radius: 50%;
  background: radial-gradient(circle at 38% 35%, rgba(14,124,90,.18) 0%, rgba(14,124,90,.04) 100%);
  border: 1px solid ${C.accent}40;
  display: flex; align-items: center; justify-content: center;
  font-size: 38px;
  position: relative; z-index: 1;
  box-shadow: 0 0 40px ${C.accentGlow}, inset 0 1px 0 rgba(22,40,63,.06);
  animation: drift 6s ease-in-out infinite;
}

/* ── Scan line effect inside shield ── */
.shield-scan {
  position: absolute; inset: 0; border-radius: 50%; overflow: hidden;
  pointer-events: none; z-index: 2;
}
.shield-scan::after {
  content: '';
  position: absolute; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, ${C.accent}60, transparent);
  animation: scanLine 3s ease-in-out infinite 1s;
}

/* ── Pillar cards ── */
.pillar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
}
.pillar-card {
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 16px;
  padding: 26px 24px;
  transition: border-color .22s, transform .22s, box-shadow .22s;
  position: relative; overflow: hidden;
}
.pillar-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, var(--card-accent), transparent);
  opacity: 0; transition: opacity .3s;
}
.pillar-card:hover { border-color: ${C.borderHover}; transform: translateY(-3px); box-shadow: 0 16px 40px rgba(22,40,63,.10); }
.pillar-card:hover::before { opacity: 1; }
.pillar-icon {
  width: 46px; height: 46px; border-radius: 13px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; margin-bottom: 18px;
  position: relative;
}

/* ── Rights accordion ── */
.right-row {
  border-bottom: 1px solid ${C.border}60;
  transition: background .15s;
}
.right-row:last-child { border-bottom: none; }
.right-header {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 20px; cursor: pointer;
  transition: background .15s;
  border-radius: 10px;
}
.right-header:hover { background: rgba(22,40,63,.03); }
.right-body {
  padding: 0 20px 18px 58px;
  font-size: 13px; color: ${C.textMid}; line-height: 1.75;
  animation: fadeIn .2s ease both;
}

/* ── Data flow diagram ── */
.flow-track {
  display: flex; align-items: center; gap: 0;
  overflow-x: auto; padding-bottom: 8px;
  scrollbar-width: none;
}
.flow-track::-webkit-scrollbar { display: none; }
.flow-node {
  flex-shrink: 0;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 12px;
  padding: 14px 16px;
  text-align: center;
  min-width: 110px;
  position: relative;
  transition: border-color .2s;
  animation: nodePulse 3s ease-in-out infinite;
}
.flow-node:hover { border-color: ${C.accent}50; }
.flow-arrow {
  flex-shrink: 0;
  width: 32px; height: 2px;
  background: linear-gradient(90deg, ${C.border}, ${C.accent}60);
  position: relative;
  margin: 0 2px;
}
.flow-arrow::after {
  content: '';
  position: absolute; right: -1px; top: 50%;
  transform: translateY(-50%);
  border-left: 6px solid ${C.accent}60;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

/* ── Commitment strip ── */
.commit-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1px;
  background: ${C.border};
  border: 1px solid ${C.border};
  border-radius: 14px;
  overflow: hidden;
}
.commit-cell {
  background: ${C.surface};
  padding: 24px 20px;
  text-align: center;
  transition: background .2s;
}
.commit-cell:hover { background: ${C.surfaceAlt}; }

/* ── Contact card ── */
.contact-card {
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 16px;
  padding: 28px 32px;
  display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
  position: relative; overflow: hidden;
}

/* ── Language switcher ── */
.lang-switcher { position:relative; display:inline-flex; align-items:center; }
.lang-btn {
  display:inline-flex; align-items:center; gap:6px;
  background:transparent; border:1px solid ${C.border};
  border-radius:8px; padding:6px 10px;
  font-size:12px; font-weight:600; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:border-color .2s, color .2s, background .2s; white-space:nowrap;
}
.lang-btn:hover { border-color:${C.borderHover}; color:${C.text}; background:rgba(22,40,63,.04); }
.lang-dropdown {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:12px; padding:6px;
  min-width:180px; z-index:400;
  box-shadow:0 16px 40px rgba(22,40,63,.16);
  animation:fadeUp .2s ease both;
}
.lang-option {
  display:flex; align-items:center; gap:10px;
  padding:9px 12px; border-radius:8px;
  font-size:13px; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:background .15s, color .15s;
}
.lang-option:hover { background:${C.surfaceAlt}; color:${C.text}; }
.lang-option.active { color:${C.accent}; background:${C.accentDim}; }

/* Hide Google's default toolbar */
.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
body { top:0 !important; }
.skiptranslate { display:none !important; }

/* ── Scrollbar ── */
::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Responsive ── */
@media(max-width:640px) {
  .pillar-grid { grid-template-columns: 1fr; }
  .commit-strip { grid-template-columns: 1fr 1fr; }
  .flow-track { justify-content: flex-start; }
  .contact-card { flex-direction: column; align-items: flex-start; }
}
@media(max-width:400px) {
  .commit-strip { grid-template-columns: 1fr; }
}
`;

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
───────────────────────────────────────────── */
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{fontSize:"14px"}}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{fontSize:"9px", opacity:.6, marginLeft:"2px"}}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{fontSize:"10px", fontWeight:"600", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px"}}>
            Select Language
          </div>
          {LANGUAGES.map(l => (
            <div
              key={l.code}
              className={"lang-option" + (l.code === current ? " active" : "")}
              onClick={() => select(l.code)}
            >
              <span style={{fontSize:"16px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{marginLeft:"auto", fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function PublicNav() {
  return (
    <nav className="tf-nav">
      <a href="/" style={{display:"flex", alignItems:"center"}}>
        <img src={logo} alt="Optionstock" style={{height:"90px", width:"auto"}} />
      </a>
      <div className="tf-nav-links">
        <a href="/"        className="tf-nav-link">Home</a>
        <a href="/about"   className="tf-nav-link">About</a>
        <a href="/news"    className="tf-nav-link">News</a>
        <a href="/privacy" className="tf-nav-link active">Privacy</a>
      </div>
      <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
        <LanguageSwitcher />
        <a href="/login"  className="btn-ghost">Log in</a>
        <a href="/signup" className="btn-primary">Sign up free →</a>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
function Hero() {
  return (
    <div style={{
      textAlign:"center",
      padding:"clamp(56px,8vw,100px) clamp(16px,6vw,48px) clamp(40px,5vw,60px)",
      position:"relative", zIndex:1,
      animation:"fadeUp .7s cubic-bezier(.22,1,.36,1) both",
    }}>
      {/* Glow */}
      <div style={{position:"absolute", width:"600px", height:"300px", borderRadius:"50%", background:C.accentGlow, filter:"blur(110px)", top:0, left:"50%", transform:"translateX(-50%)", pointerEvents:"none", zIndex:0}}/>

      <div style={{position:"relative", zIndex:1}}>
        {/* Animated shield */}
        <div className="shield-wrap">
          <div className="shield-ring" style={{width:"160px", height:"160px"}}/>
          <div className="shield-ring" style={{width:"130px", height:"130px"}}/>
          <div className="shield-ring" style={{width:"104px", height:"104px"}}/>
          <div className="shield-icon">
            🛡️
            <div className="shield-scan"/>
          </div>
        </div>

        <div className="badge" style={{marginBottom:"20px"}}>Privacy Centre</div>

        <h1 style={{
          fontFamily:"'Fraunces',serif",
          fontSize:"clamp(36px,6vw,68px)",
          fontWeight:"800",
          letterSpacing:"-2.5px",
          lineHeight:"1.04",
          color:C.text,
          marginBottom:"20px",
          maxWidth:"680px",
          marginLeft:"auto", marginRight:"auto",
        }}>
          Your data.<br/>
          <span style={{color:C.accent}}>Your rules.</span>
        </h1>

        <p style={{
          fontSize:"clamp(15px,2vw,17px)",
          color:C.textMid,
          lineHeight:"1.75",
          maxWidth:"500px",
          marginLeft:"auto", marginRight:"auto",
          marginBottom:"36px",
        }}>
          We built Optionstock to be the platform we always wanted — and privacy is
          not an afterthought. Here is exactly what we collect, why, and what you
          can do about it.
        </p>

        <div style={{display:"flex", gap:"12px", justifyContent:"center", flexWrap:"wrap"}}>
          <a href="/policy" className="btn-primary" style={{padding:"12px 26px", fontSize:"14px"}}>
            Read full Privacy Policy →
          </a>
          <a href="mailto:privacy@Optionstock.io" className="btn-ghost" style={{padding:"12px 22px", fontSize:"13px"}}>
            Contact DPO
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMMITMENT STRIP
───────────────────────────────────────────── */
const COMMITS = [
  { val:"0",      label:"Data sold",          sub:"We never sell your data",     color:C.accent  },
  { val:"256-bit", label:"Encryption",        sub:"TLS in transit, AES at rest", color:C.blue    },
  { val:"UK GDPR", label:"Compliant",         sub:"FCA registered, ICO certified",color:C.purple },
  { val:"30 days", label:"DSAR response",     sub:"Data requests answered fast",  color:C.green  },
];

function CommitStrip() {
  return (
    <div style={{maxWidth:"900px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(48px,6vw,72px)", position:"relative", zIndex:1}}>
      <div className="commit-strip">
        {COMMITS.map(({ val, label, sub, color }, i) => (
          <div key={label} className="commit-cell" style={{animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.1+i*.08}s both`}}>
            <div style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,3vw,30px)", fontWeight:"800", color, letterSpacing:"-1px", marginBottom:"4px"}}>{val}</div>
            <div style={{fontSize:"12px", color:C.text, fontWeight:"700", marginBottom:"3px"}}>{label}</div>
            <div style={{fontSize:"11px", color:C.textDim, fontFamily:"'IBM Plex Mono',monospace"}}>{sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRIVACY PILLARS
───────────────────────────────────────────── */
const PILLARS = [
  {
    icon:"🔒", color:C.accent,
    title:"Minimal collection",
    desc:"We collect only what we need to run the platform — no more. No browsing histories, no device fingerprinting beyond security, no shadow profiles.",
  },
  {
    icon:"🎯", color:C.blue,
    title:"Purpose-bound use",
    desc:"Data you give us is used only for the reason we stated. Trading data stays in the platform. Identity data is used for KYC only. We don't cross the streams.",
  },
  {
    icon:"🚫", color:C.red,
    title:"Zero ad tracking",
    desc:"We run no ad network pixels, no third-party retargeting, no data broker feeds. Our business model is subscriptions and performance fees — not selling you.",
  },
  {
    icon:"🔐", color:C.purple,
    title:"Encrypted end-to-end",
    desc:"Your data is encrypted in transit (TLS 1.3) and at rest (AES-256). Sensitive fields like API keys and bank details use additional envelope encryption.",
  },
  {
    icon:"🌍", color:C.green,
    title:"UK-based storage",
    desc:"Your personal data is stored on AWS infrastructure in eu-west-2 (London). Data transfers outside the UK follow SCCs and adequacy decisions.",
  },
  {
    icon:"⚡", color:C.amber,
    title:"You're in control",
    desc:"Download everything, delete everything, or restrict processing — all from your account settings. Requests are processed within 30 days, usually faster.",
  },
];

function Pillars() {
  return (
    <div style={{maxWidth:"900px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(56px,7vw,88px)", position:"relative", zIndex:1}}>
      <div style={{textAlign:"center", marginBottom:"40px", animation:"fadeUp .6s cubic-bezier(.22,1,.36,1) both"}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.accent, letterSpacing:".1em", textTransform:"uppercase", marginBottom:"12px"}}>How we think about it</div>
        <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(24px,3.5vw,34px)", fontWeight:"800", letterSpacing:"-1.2px", color:C.text}}>
          Six principles, no asterisks.
        </h2>
      </div>
      <div className="pillar-grid">
        {PILLARS.map(({ icon, color, title, desc }, i) => (
          <div
            key={title}
            className="pillar-card"
            style={{"--card-accent": color, animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.08+i*.08}s both`}}
          >
            <div className="pillar-icon" style={{background:`${color}15`, border:`1px solid ${color}30`}}>
              <span style={{fontSize:"22px"}}>{icon}</span>
            </div>
            <div style={{fontFamily:"'Fraunces',serif", fontSize:"15px", fontWeight:"700", color:C.text, marginBottom:"10px", letterSpacing:"-0.2px"}}>{title}</div>
            <div style={{fontSize:"13px", color:C.textMid, lineHeight:"1.75"}}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DATA FLOW SECTION
───────────────────────────────────────────── */
const FLOW_NODES = [
  { icon:"👤", label:"You",           sub:"Account holder",    delay:0    },
  { icon:"🔐", label:"Encrypted",     sub:"TLS 1.3 in transit",delay:.15  },
  { icon:"🏦", label:"Optionstock",     sub:"UK servers only",   delay:.3   },
  { icon:"🔒", label:"Encrypted",     sub:"AES-256 at rest",   delay:.45  },
  { icon:"📊", label:"Your platform", sub:"Nothing leaves",    delay:.6   },
];

function DataFlow() {
  return (
    <div style={{maxWidth:"900px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(56px,7vw,88px)", position:"relative", zIndex:1}}>
      <div style={{
        background:`linear-gradient(135deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
        border:`1px solid ${C.border}`,
        borderRadius:"18px",
        padding:"clamp(28px,4vw,44px)",
        position:"relative", overflow:"hidden",
      }}>
        {/* Glow */}
        <div style={{position:"absolute", width:"300px", height:"200px", borderRadius:"50%", background:C.accentGlow, filter:"blur(80px)", bottom:"-80px", right:"-40px", pointerEvents:"none"}}/>

        <div style={{position:"relative", zIndex:1}}>
          <div style={{marginBottom:"28px"}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.accent, letterSpacing:".1em", textTransform:"uppercase", marginBottom:"10px"}}>Data journey</div>
            <h3 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(18px,2.5vw,24px)", fontWeight:"800", letterSpacing:"-0.8px", color:C.text}}>
              Where your data goes — and where it doesn't.
            </h3>
          </div>

          <div className="flow-track">
            {FLOW_NODES.map(({ icon, label, sub, delay }, i) => (
              <React.Fragment key={label}>
                <div className="flow-node" style={{animation:`fadeUp .5s cubic-bezier(.22,1,.36,1) ${delay}s both`, animationDelay:`${delay + .3}s`}}>
                  <div style={{fontSize:"22px", marginBottom:"8px"}}>{icon}</div>
                  <div style={{fontSize:"12px", fontWeight:"700", color:C.text, marginBottom:"3px"}}>{label}</div>
                  <div style={{fontSize:"10px", color:C.textDim, fontFamily:"'IBM Plex Mono',monospace"}}>{sub}</div>
                </div>
                {i < FLOW_NODES.length - 1 && <div className="flow-arrow"/>}
              </React.Fragment>
            ))}
          </div>

          <div style={{marginTop:"20px", padding:"14px 16px", background:`${C.accentDim}`, border:`1px solid ${C.accent}25`, borderRadius:"10px", display:"flex", alignItems:"center", gap:"10px"}}>
            <span style={{fontSize:"16px"}}>✅</span>
            <p style={{fontSize:"13px", color:C.textMid, lineHeight:"1.6"}}>
              Your data <strong style={{color:C.text}}>never</strong> passes through advertising networks, data brokers, or any third party not listed in our{" "}
              <a href="/policy#data-sharing" style={{color:C.accent, fontWeight:"600"}}>Data Sharing section</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   YOUR RIGHTS ACCORDION
───────────────────────────────────────────── */
const RIGHTS = [
  {
    icon:"📥", color:C.accent, label:"Access your data",
    desc:"Request a complete copy of all personal data we hold about you, including your trading history, account info, and communication records. We'll deliver it within 30 days in a machine-readable format.",
  },
  {
    icon:"✏️", color:C.blue, label:"Correct inaccuracies",
    desc:"Found something wrong? Update your profile directly in Settings, or contact us and we'll correct any inaccurate or incomplete data promptly.",
  },
  {
    icon:"🗑️", color:C.red, label:"Delete your account & data",
    desc:"Request full erasure of your data where legally permitted. Note: we're required to retain transaction records for 7 years under FCA regulations — but everything else goes.",
  },
  {
    icon:"⏸️", color:C.purple, label:"Restrict processing",
    desc:"Ask us to pause processing your data in certain circumstances — for example, while a dispute is being resolved or if you contest the accuracy of data we hold.",
  },
  {
    icon:"📦", color:C.green, label:"Take your data elsewhere",
    desc:"Export your Optionstock data in JSON or CSV format and take it to another platform. Your trading history, preferences, and profile are yours to keep.",
  },
  {
    icon:"🙅", color:C.amber, label:"Object to processing",
    desc:"Object to us processing your data for legitimate interests — including profiling. We'll stop unless we can demonstrate compelling legitimate grounds that override your interests.",
  },
];

function RightsSection() {
  const [open, setOpen] = useState(null);

  return (
    <div style={{maxWidth:"760px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(56px,7vw,88px)", position:"relative", zIndex:1}}>
      <div style={{textAlign:"center", marginBottom:"40px", animation:"fadeUp .6s cubic-bezier(.22,1,.36,1) both"}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.accent, letterSpacing:".1em", textTransform:"uppercase", marginBottom:"12px"}}>UK GDPR</div>
        <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(24px,3.5vw,34px)", fontWeight:"800", letterSpacing:"-1.2px", color:C.text}}>
          Your rights — all of them.
        </h2>
        <p style={{fontSize:"14px", color:C.textMid, marginTop:"12px", lineHeight:"1.65"}}>
          Under UK GDPR you have six rights over your personal data. Click any right to learn how to exercise it.
        </p>
      </div>

      <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:"16px", overflow:"hidden", animation:"fadeUp .6s cubic-bezier(.22,1,.36,1) .1s both"}}>
        {RIGHTS.map(({ icon, color, label, desc }, i) => (
          <div key={label} className="right-row">
            <div className="right-header" onClick={() => setOpen(open === i ? null : i)}>
              <div style={{
                width:"32px", height:"32px", borderRadius:"9px", flexShrink:0,
                background:`${color}18`, border:`1px solid ${color}30`,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:"16px",
              }}>{icon}</div>
              <div style={{flex:1, fontFamily:"'Fraunces',serif", fontSize:"14px", fontWeight:"700", color:C.text, letterSpacing:"-0.2px"}}>
                {label}
              </div>
              <div style={{fontSize:"12px", color:C.textDim, fontWeight:"600", transition:"transform .2s", transform: open === i ? "rotate(180deg)" : "rotate(0deg)"}}>▼</div>
            </div>
            {open === i && (
              <div className="right-body">{desc}</div>
            )}
          </div>
        ))}
      </div>

      <div style={{marginTop:"20px", textAlign:"center", fontSize:"13px", color:C.textDim}}>
        Submit a request at{" "}
        <a href="mailto:privacy@Optionstock.io" style={{color:C.accent, fontWeight:"600"}}>privacy@Optionstock.io</a>
        {" "}— we respond within 30 days.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   WHAT WE DON'T DO
───────────────────────────────────────────── */
const DONTS = [
  { icon:"🚫", text:"Sell your personal data to advertisers or data brokers" },
  { icon:"🚫", text:"Share your trading activity with third parties for commercial gain" },
  { icon:"🚫", text:"Use your financial data to build advertising profiles" },
  { icon:"🚫", text:"Store data outside the UK without adequate protections in place" },
  { icon:"🚫", text:"Retain your personal data longer than legally required" },
  { icon:"🚫", text:"Make automated decisions that significantly affect you without human review" },
];

function WeDont() {
  return (
    <div style={{maxWidth:"760px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(56px,7vw,88px)", position:"relative", zIndex:1}}>
      <div style={{
        background:`linear-gradient(135deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
        border:`1px solid ${C.border}`,
        borderLeft:`3px solid ${C.red}`,
        borderRadius:"16px",
        padding:"clamp(24px,3.5vw,40px)",
        position:"relative", overflow:"hidden",
      }}>
        <div style={{position:"absolute", width:"200px", height:"200px", borderRadius:"50%", background:"rgba(192,57,43,.08)", filter:"blur(60px)", top:"-60px", right:"-40px", pointerEvents:"none"}}/>
        <div style={{position:"relative", zIndex:1}}>
          <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.red, letterSpacing:".1em", textTransform:"uppercase", marginBottom:"10px"}}>Non-negotiables</div>
          <h3 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(18px,2.5vw,24px)", fontWeight:"800", letterSpacing:"-0.8px", color:C.text, marginBottom:"24px"}}>
            Things we will never do.
          </h3>
          <div style={{display:"flex", flexDirection:"column", gap:"10px"}}>
            {DONTS.map(({ icon, text }, i) => (
              <div key={i} style={{display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", background:"rgba(192,57,43,.04)", border:"1px solid rgba(192,57,43,.12)", borderRadius:"10px"}}>
                <span style={{fontSize:"16px", flexShrink:0}}>{icon}</span>
                <span style={{fontSize:"13px", color:C.textMid, lineHeight:"1.6"}}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CONTACT / DPO SECTION
───────────────────────────────────────────── */
function ContactSection() {
  return (
    <div style={{maxWidth:"900px", margin:"0 auto", padding:"0 clamp(16px,5vw,48px) clamp(72px,9vw,110px)", position:"relative", zIndex:1}}>
      <div className="contact-card">
        <div style={{position:"absolute", width:"300px", height:"200px", borderRadius:"50%", background:C.accentGlow, filter:"blur(80px)", bottom:"-60px", right:"0", pointerEvents:"none"}}/>

        <div style={{
          width:"56px", height:"56px", borderRadius:"16px", flexShrink:0,
          background:C.accentDim, border:`1px solid ${C.accent}40`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:"26px",
          position:"relative", zIndex:1,
        }}>👩‍⚖️</div>

        <div style={{flex:1, minWidth:"200px", position:"relative", zIndex:1}}>
          <div style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(16px,2vw,19px)", fontWeight:"800", color:C.text, marginBottom:"6px", letterSpacing:"-0.3px"}}>
            Talk to our Data Protection Officer
          </div>
          <div style={{fontSize:"13px", color:C.textMid, lineHeight:"1.65", marginBottom:"4px"}}>
            Questions, concerns, or a formal complaint — our DPO handles all privacy matters personally.
          </div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"12px", color:C.accent, marginTop:"8px"}}>
            privacy@Optionstock.io · Responds within 30 days
          </div>
        </div>

        <div style={{display:"flex", gap:"10px", flexShrink:0, position:"relative", zIndex:1, flexWrap:"wrap"}}>
          <a href="/policy" className="btn-ghost" style={{padding:"11px 18px", fontSize:"13px"}}>Full Policy →</a>
          <a href="mailto:privacy@Optionstock.io" className="btn-primary" style={{padding:"11px 22px", fontSize:"13px"}}>Email DPO</a>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */
const Privacy = () => (
  <>
    <style>{CSS}</style>
    <div className="grid-bg"/>
    <PublicNav/>
    <div style={{position:"relative"}}>
      <Hero/>
      <CommitStrip/>
      <Pillars/>
      <DataFlow/>
      <RightsSection/>
      <WeDont/>
      <ContactSection/>
    </div>
  </>
);

export default Privacy;